import type { FlowGraph } from '../api/contracts';
import { useMonitorApi } from '../app/api-context';
import { REFRESH_INTERVAL_MS } from '../lib/auto-refresh';
import { usePolledResource } from './use-polled-resource';
import type { PolledResource } from './use-polled-resource';

export interface LiveFlowGraphOptions {
  /**
   * Actualización automática, encendida por defecto. Apagada, el grafo igual se
   * carga al montar, al cambiar de corrida y con `reload()`: lo que se detiene
   * es el refresco cada 2 s.
   */
  readonly enabled?: boolean;
}

export type LiveFlowGraph = PolledResource<FlowGraph>;

/**
 * El grafo de una corrida, refrescado cada 2 s mientras se mira.
 *
 * Consulta en serie: una respuesta lenta nunca se superpone con la siguiente ni
 * pisa a una corrida más nueva (las garantías completas están en
 * `usePolledResource`). Cambiar de corrida vacía el grafo de inmediato: el de la
 * anterior es otro documento y dibujarlo bajo el id nuevo confundiría. Con
 * `correlationId` en `null` (todavía no se sabe de qué corrida se trata) no
 * consulta.
 */
export function useLiveFlowGraph(
  correlationId: string | null,
  options: LiveFlowGraphOptions = {},
): LiveFlowGraph {
  const api = useMonitorApi();
  return usePolledResource(
    correlationId === null ? null : () => api.getFlowGraph(correlationId),
    [api, correlationId],
    {
      intervalMs: REFRESH_INTERVAL_MS.flowGraph,
      enabled: options.enabled ?? true,
      keepPreviousData: false,
    },
  );
}
